import { Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { SafeHtml } from '@/components/shared/SafeHtml';
import { usePromotionalBanner } from '@/hooks/usePromotionalBanner';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { useAuthStore } from '@/stores/auth-store';

/**
 * If the promo HTML contains a single <a> wrapping all text
 * (e.g. "<a href='...'>Plain text. CTA!</a>"), pull the plain text outside the
 * link so only the last sentence (the CTA like "Explore Now!") is underlined.
 */
export function splitPromoLink(html: string): string {
  // Match an <a ...>...</a> anywhere in the string, capturing the opening tag and inner text
  const match = html.match(/(<a\s[^]*?>)([^]*?)<\/a>/i);
  if (!match) return html;

  const fullMatch = match[0]; // full <a ...>...</a>
  const aOpen = match[1]; // <a href="...">
  const inner = match[2]; // inner text content
  const lastDotSpace = inner.lastIndexOf('. ');

  if (lastDotSpace === -1) return html;

  const plainPart = inner.slice(0, lastDotSpace + 2);
  const ctaPart = inner.slice(lastDotSpace + 2);

  return html.replace(fullMatch, `${plainPart}${aOpen}${ctaPart}</a>`);
}

export function PromotionalBanner() {
  const { t } = useTranslation();
  const { isVisible, bannerType, bannerHtml, daysLeft, showUpgrade, dismiss } =
    usePromotionalBanner();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);

  if (!isVisible) return null;
  if (bannerType !== 'trial' && !bannerHtml) return null;

  const handleUpgrade = () => {
    analyticsUpgradeCta({
      ctaLocation: `promotional_banner_${bannerType}`,
      currentPlan: company?.payment?.plan,
    });
    openSubscriptionPlan({
      title: t('Change Plan'),
      subtitle: t('Upgrade Required'),
      action: bannerType === 'trial' ? 'changePlanTrialToPay' : 'changePlanPay',
      sizeModal: 'xl',
      params: {
        arrCssFile: true,
        // Match scio1 dashboard.js params: filters Free plan (order >= standard)
        // and loads pricing data via getPlanFeatures() in subscriptionPlanModal.js
        ...(bannerType === 'trial' && {
          planFeature: 'proPlus',
          minPlanRequired: 'standard',
        }),
      },
    });
  };

  if (bannerType === 'trial') {
    return (
      <div
        className="hidden sm:flex w-full items-center justify-center bg-gradient-to-r from-[#17a085] via-primary to-[#1ec9a8] px-6 py-2 text-sm text-white"
        role="alert"
      >
        <div className="flex min-w-0 items-center gap-3">
          <Info className="h-5 w-5 shrink-0" />
          <div className="flex flex-col leading-tight">
            <span className="truncate font-bold">
              {t('waitingForTrial', { getDayTrial: daysLeft })}
            </span>
            {bannerHtml && (
              <SafeHtml
                html={splitPromoLink(bannerHtml)}
                as="span"
                className="hidden lg:inline truncate text-xs font-normal text-white/90 [&_a]:text-white [&_a]:underline"
              />
            )}
          </div>
          {showUpgrade && (
            <>
              <span className="hidden md:block h-5 w-px rounded-full bg-white" />
              <Button
                size="sm"
                className="hidden md:inline-flex shrink-0 rounded-md bg-foreground px-5 text-xs font-semibold text-background hover:bg-foreground/90"
                onClick={handleUpgrade}
              >
                {t('Upgrade Now')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="hidden sm:flex h-10 w-full items-center gap-3 bg-primary px-6 text-sm font-medium text-white [&_a]:text-white [&_a]:no-underline [&_a]:hover:underline [&_p]:mb-0 [&_p]:text-sm"
      role="alert"
    >
      {/* Spacer to balance dismiss button */}
      {bannerType === 'paid' && <div className="w-6 shrink-0" />}

      <div className="mx-auto flex min-w-0 items-center gap-3">
        {/* Promotional HTML */}
        <SafeHtml html={bannerHtml ?? ''} as="span" className="truncate" />

        {/* Upgrade button — free plan (admin only) */}
        {showUpgrade && (
          <>
            <span className="hidden md:block h-5 w-px bg-white/30" />
            <Button
              size="sm"
              className="hidden md:inline-flex h-7 rounded-md bg-foreground px-4 text-xs text-background hover:bg-foreground/90"
              onClick={(e) => {
                e.stopPropagation();
                handleUpgrade();
              }}
            >
              {t('Upgrade')} 👑
            </Button>
          </>
        )}
      </div>

      {/* Close button — paid users only, never for trial/free */}
      {bannerType === 'paid' && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="-mr-3 shrink-0 rounded-full p-1 text-white transition-colors hover:bg-white/15"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          aria-label={t('Dismiss banner')}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      )}
    </div>
  );
}
